'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Dialog } from 'src/components/PulseUI/Dialog';
import { PulseButton } from 'src/components/PulseUI/PulseButton';

export interface DeleteRouteDialogProps {
  isOpen: boolean;
  routeLabel: string;
  isDeleting: boolean;
  /** API error from a failed delete; the dialog stays open to show it */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteRouteDialog: React.FC<DeleteRouteDialogProps> = ({
  isOpen,
  routeLabel,
  isDeleting,
  error = null,
  onConfirm,
  onCancel,
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      aria-labelledby="delete-route-dialog-title"
      data-testid="delete-route-dialog"
    >
      <Dialog.Header>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50">
            <Icon icon={faTrash} className="text-red-500" />
          </div>
          <div className="text-lg font-medium" id="delete-route-dialog-title">
            Delete Route
          </div>
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div
          className="text-sm text-neutral-700 p-4"
          role="alert"
          data-testid="delete-route-dialog-message"
        >
          Are you sure you want to delete <strong>{routeLabel}</strong>? This
          action cannot be undone.
        </div>
        {error && (
          <p
            className="px-4 pb-4 text-sm text-red-600"
            role="alert"
            data-testid="delete-route-dialog-error"
          >
            {error}
          </p>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <PulseButton
          variant="outline"
          onClick={onCancel}
          disabled={isDeleting}
          aria-label="Cancel route deletion"
          className="w-full rounded-full"
          data-testid="delete-route-dialog-cancel"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="danger"
          onClick={onConfirm}
          loading={isDeleting}
          aria-label="Confirm route deletion"
          aria-busy={isDeleting}
          className="w-full rounded-full"
          data-testid="delete-route-dialog-confirm"
        >
          Delete
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default DeleteRouteDialog;
